import React from "react";
import {
  MdBusiness,
  MdEventAvailable,
  MdEvent,
  MdEventBusy,
  MdPersonAdd,
  MdReceipt,
  MdFlashOn,
  MdLock,
} from "react-icons/md";
import Card from "components/card";
import Widget from "components/widget/Widget";
import { api } from "utils/api";

const NIVEIS = {
  alto: ["Alto", "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"],
  medio: ["Médio", "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"],
  baixo: ["Baixo", "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"],
  inativo: ["Inativo", "bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300"],
};

const MODULOS = [
  ["agenda", "Agenda"],
  ["financeiro", "Receitas"],
  ["pagar", "Pagar"],
  ["clientes", "Clientes"],
  ["fichas", "Fichas"],
  ["estoque", "Estoque"],
];

const selectClasse =
  "h-10 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white";

function variacao(atual, anterior, inverter = false, sufixo = "") {
  if (anterior === null || anterior === undefined || atual === null) return null;
  if (anterior === 0) return atual === 0 ? null : { texto: "novo", bom: !inverter };
  const pct = Math.round(((atual - anterior) / anterior) * 100);
  if (pct === 0) return { texto: "= período anterior", bom: null };
  const subiu = pct > 0;
  return { texto: `${subiu ? "▲" : "▼"} ${Math.abs(pct)}%${sufixo}`, bom: inverter ? !subiu : subiu };
}

function ValorComVariacao({ valor, v }) {
  return (
    <span>
      {valor}
      {v && (
        <span
          className={`ml-2 text-xs font-medium ${
            v.bom === null ? "text-gray-500" : v.bom ? "text-green-600" : "text-red-500"
          }`}
        >
          {v.texto}
        </span>
      )}
    </span>
  );
}

function Barra({ rotulo, valor, total, extra }) {
  const pct = total ? Math.round((valor / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="mb-1 flex justify-between text-sm text-navy-700 dark:text-white">
        <span>{rotulo}</span>
        <span className="text-gray-600 dark:text-gray-400">
          {extra || `${valor} de ${total}`} <span className="ml-1 font-semibold">{pct}%</span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-white/10">
        <div className="h-2 rounded-full bg-brand-500 dark:bg-brand-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function GraficoSemanal({ serie }) {
  const max = Math.max(1, ...serie.map((s) => s.atendimentos));
  const larg = 600;
  const alt = 160;
  const passo = larg / serie.length;
  return (
    <svg viewBox={`0 0 ${larg} ${alt + 28}`} className="w-full">
      {serie.map((s, i) => {
        const h = Math.round((s.atendimentos / max) * alt);
        const x = i * passo + passo * 0.15;
        return (
          <g key={s.semana}>
            <rect x={x} y={alt - h} width={passo * 0.7} height={Math.max(h, 1)} rx="4" fill="#9b5de5" opacity={s.atendimentos ? 1 : 0.25}>
              <title>{`Semana de ${s.semana.split("-").reverse().join("/")}: ${s.atendimentos} atendimentos, ${s.empresasAtivas} empresa(s)`}</title>
            </rect>
            <text x={x + passo * 0.35} y={alt - h - 4} textAnchor="middle" fontSize="11" fill="currentColor" className="text-gray-600 dark:text-gray-300">
              {s.atendimentos || ""}
            </text>
            <text x={x + passo * 0.35} y={alt + 16} textAnchor="middle" fontSize="10" fill="currentColor" className="text-gray-500">
              {s.semana.slice(8, 10)}/{s.semana.slice(5, 7)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function Relatorios() {
  const [filtros, setFiltros] = React.useState({ dias: 30, escopo: "todas", equipe: "todas", conta: "todas", normalizar: false });
  const [dados, setDados] = React.useState(null);
  const [carregando, setCarregando] = React.useState(true);
  const [erro, setErro] = React.useState("");

  React.useEffect(() => {
    let cancelado = false;
    (async () => {
      setCarregando(true);
      setErro("");
      const params = new URLSearchParams({
        dias: filtros.dias,
        escopo: filtros.escopo,
        equipe: filtros.equipe,
        conta: filtros.conta,
        normalizar: filtros.normalizar ? "profissional" : "nao",
      });
      const data = await api("/api/admin/relatorios?" + params.toString());
      if (cancelado || !data) return;
      if (!data.success) setErro(data.error || "Não foi possível gerar o relatório.");
      else setDados(data);
      setCarregando(false);
    })();
    return () => {
      cancelado = true;
    };
  }, [filtros]);

  const set = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: campo === "dias" ? Number(e.target.value) : e.target.value }));

  const k = dados?.kpis;
  const dist = dados?.distribuicao;
  const maxHist = dist && !dist.oculta ? Math.max(1, ...dist.histograma.map((h) => h.empresas)) : 1;

  return (
    <div>
      <Card extra="mt-3 w-full p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Período
            <select className={`${selectClasse} mt-1 w-full`} value={filtros.dias} onChange={set("dias")}>
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Contas
            <select className={`${selectClasse} mt-1 w-full`} value={filtros.escopo} onChange={set("escopo")}>
              <option value="todas">Todas</option>
              <option value="pagantes">Pagantes e em trial (sem free)</option>
              <option value="free">Só parceiros (free)</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Tamanho da equipe
            <select className={`${selectClasse} mt-1 w-full`} value={filtros.equipe} onChange={set("equipe")}>
              <option value="todas">Todos</option>
              <option value="solo">Solo (1 pessoa)</option>
              <option value="pequena">Pequena (2–3)</option>
              <option value="grande">Grande (4+)</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            Tempo de conta
            <select className={`${selectClasse} mt-1 w-full`} value={filtros.conta} onChange={set("conta")}>
              <option value="todas">Todas</option>
              <option value="nova">Menos de 30 dias</option>
              <option value="media">30 a 90 dias</option>
              <option value="antiga">Mais de 90 dias</option>
            </select>
          </label>
          <label className="flex items-end gap-2 pb-2 text-sm text-navy-700 dark:text-white">
            <input
              type="checkbox"
              checked={filtros.normalizar}
              onChange={(e) => setFiltros((f) => ({ ...f, normalizar: e.target.checked }))}
            />
            Normalizar por profissional
          </label>
        </div>
        <p className="mt-3 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
          <MdLock className="mt-0.5 h-4 w-4 shrink-0" />
          {dados?.politica ||
            "Relatório só com contagens agregadas: nenhum dado de cliente final e nenhum valor em R$ dos estúdios é lido."}
        </p>
      </Card>

      {erro && <p className="mt-4 text-sm text-red-500">{erro}</p>}
      {carregando && !dados && <p className="mt-4 text-gray-500">Carregando...</p>}

      {dados?.vazio && (
        <Card extra="mt-5 w-full p-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">Nenhuma empresa corresponde a esses filtros.</p>
        </Card>
      )}

      {dados && !dados.vazio && (
        <div className={carregando ? "opacity-60 transition" : "transition"}>
          <p className="mt-4 text-xs text-gray-600 dark:text-gray-400">
            {dados.amostra} empresa(s) no filtro · período {dados.periodo.inicio.split("-").reverse().join("/")} a{" "}
            {dados.periodo.fim.split("-").reverse().join("/")} · comparação com os {filtros.dias} dias anteriores
          </p>

          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Widget icon={<MdBusiness className="h-6 w-6" />} title="Empresas com atividade" subtitle={<ValorComVariacao valor={`${k.empresasAtivas.atual} de ${dados.totalEmpresas}`} v={variacao(k.empresasAtivas.atual, k.empresasAtivas.anterior)} />} />
            <Widget icon={<MdEventAvailable className="h-6 w-6" />} title="Atendimentos concluídos" subtitle={<ValorComVariacao valor={k.atendimentosConcluidos.atual} v={variacao(k.atendimentosConcluidos.atual, k.atendimentosConcluidos.anterior)} />} />
            <Widget icon={<MdEvent className="h-6 w-6" />} title="Agendamentos marcados" subtitle={<ValorComVariacao valor={k.agendamentosMarcados.atual} v={variacao(k.agendamentosMarcados.atual, k.agendamentosMarcados.anterior)} />} />
            <Widget icon={<MdEventBusy className="h-6 w-6" />} title="Taxa de cancelamento" subtitle={<ValorComVariacao valor={k.taxaCancelamento.atual === null ? "—" : `${k.taxaCancelamento.atual}%`} v={variacao(k.taxaCancelamento.atual, k.taxaCancelamento.anterior, true)} />} />
            <Widget icon={<MdPersonAdd className="h-6 w-6" />} title="Clientes novos cadastrados" subtitle={<ValorComVariacao valor={k.clientesNovos.atual} v={variacao(k.clientesNovos.atual, k.clientesNovos.anterior)} />} />
            <Widget icon={<MdReceipt className="h-6 w-6" />} title="Lançamentos financeiros" subtitle={<ValorComVariacao valor={k.lancamentosFinanceiros.atual} v={variacao(k.lancamentosFinanceiros.atual, k.lancamentosFinanceiros.anterior)} />} />
            <Widget icon={<MdFlashOn className="h-6 w-6" />} title="Online agora (15 min)" subtitle={`${k.onlineAgora} empresa(s)`} />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card extra="w-full p-5">
              <h5 className="mb-1 text-base font-bold text-navy-700 dark:text-white">Adoção de módulos</h5>
              <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">Empresas que usaram cada módulo no período.</p>
              {dados.adocao.map((m) => (
                <Barra key={m.chave} rotulo={m.modulo} valor={m.usam} total={m.total} />
              ))}
            </Card>

            <Card extra="w-full p-5">
              <h5 className="mb-1 text-base font-bold text-navy-700 dark:text-white">Funil de ativação</h5>
              <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">Quantas empresas já chegaram em cada etapa (acumulado, sem filtro de período).</p>
              {dados.funil.map((f) => (
                <Barra key={f.etapa} rotulo={f.etapa} valor={f.empresas} total={dados.totalEmpresas} />
              ))}
            </Card>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card extra="w-full p-5">
              <h5 className="mb-1 text-base font-bold text-navy-700 dark:text-white">
                Distribuição do volume entre empresas
              </h5>
              <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">
                Atendimentos concluídos no período{filtros.normalizar ? " por profissional" : ""}. Mediana e quartis, não média — uma empresa muito grande não distorce o resultado.
              </p>
              {dist.oculta ? (
                <p className="rounded-xl bg-lightPrimary p-3 text-sm text-gray-600 dark:bg-navy-900 dark:text-gray-300">{dist.motivo}</p>
              ) : (
                <>
                  <div className="mb-4 grid grid-cols-4 gap-2 text-center">
                    {[["P25", dist.p25], ["Mediana", dist.mediana], ["P75", dist.p75], ["Máximo", dist.maximo]].map(([r, v]) => (
                      <div key={r} className="rounded-xl bg-lightPrimary p-2 dark:bg-navy-900">
                        <p className="text-xs text-gray-600 dark:text-gray-400">{r}</p>
                        <p className="text-lg font-bold text-navy-700 dark:text-white">{v}</p>
                      </div>
                    ))}
                  </div>
                  {dist.histograma.map((h) => (
                    <Barra key={h.rotulo} rotulo={h.rotulo} valor={h.empresas} total={maxHist} extra={`${h.empresas} empresa(s)`} />
                  ))}
                </>
              )}
            </Card>

            <Card extra="w-full p-5">
              <h5 className="mb-1 text-base font-bold text-navy-700 dark:text-white">Nível de uso e recência</h5>
              <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">
                Nível calculado por profissional (ritmo equivalente a 30 dias): baixo &lt; 5, médio &lt; 40, alto ≥ 40 atendimentos por profissional.
              </p>
              <div className="mb-5 grid grid-cols-4 gap-2 text-center">
                {dados.niveis.map((n) => (
                  <div key={n.nivel} className="rounded-xl bg-lightPrimary p-2 dark:bg-navy-900">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${NIVEIS[n.nivel][1]}`}>{NIVEIS[n.nivel][0]}</span>
                    <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">{n.empresas}</p>
                  </div>
                ))}
              </div>
              <Barra rotulo="Atividade hoje" valor={dados.recencia.hoje} total={dados.totalEmpresas} />
              <Barra rotulo="Atividade há 1–7 dias" valor={dados.recencia.ate7} total={dados.totalEmpresas} />
              <Barra rotulo="Atividade há 8–30 dias" valor={dados.recencia.ate30} total={dados.totalEmpresas} />
              <Barra rotulo="Sem atividade há mais de 30 dias" valor={dados.recencia.mais30 + dados.recencia.nunca} total={dados.totalEmpresas} />
            </Card>
          </div>

          <Card extra="mt-5 w-full p-5">
            <h5 className="mb-1 text-base font-bold text-navy-700 dark:text-white">Atendimentos concluídos por semana</h5>
            <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">Últimas 12 semanas, todas as empresas do filtro somadas (passe o mouse na barra para ver o detalhe).</p>
            <GraficoSemanal serie={dados.serie} />
          </Card>

          <Card extra="mt-5 w-full px-5 pb-5">
            <h5 className="mb-1 mt-5 text-base font-bold text-navy-700 dark:text-white">Saúde de uso por empresa</h5>
            <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">Só faixa de uso, última atividade e módulos usados — sem volumes, valores ou dados de clientes finais.</p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-bold text-gray-600 dark:text-white">
                    <th className="border-b border-gray-200 pb-2 pr-4 dark:border-white/10">EMPRESA</th>
                    <th className="border-b border-gray-200 pb-2 pr-4 dark:border-white/10">PLANO</th>
                    <th className="border-b border-gray-200 pb-2 pr-4 dark:border-white/10">NÍVEL</th>
                    <th className="border-b border-gray-200 pb-2 pr-4 dark:border-white/10">ÚLTIMA ATIVIDADE</th>
                    <th className="border-b border-gray-200 pb-2 pr-4 dark:border-white/10">MÓDULOS NO PERÍODO</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.empresas.map((e) => (
                    <tr key={e.id}>
                      <td className="py-3 pr-4 text-sm font-bold text-navy-700 dark:text-white">{e.nome}</td>
                      <td className="py-3 pr-4 text-sm capitalize text-navy-700 dark:text-white">{e.plano}</td>
                      <td className="py-3 pr-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${NIVEIS[e.nivel][1]}`}>{NIVEIS[e.nivel][0]}</span>
                      </td>
                      <td className="py-3 pr-4 text-sm text-navy-700 dark:text-white">
                        {e.diasSemAtividade === null ? "sem registro" : e.diasSemAtividade === 0 ? "hoje" : `há ${e.diasSemAtividade} dia(s)`}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {MODULOS.map(([chave, rotulo]) => (
                            <span
                              key={chave}
                              className={`rounded-full px-2 py-0.5 text-xs ${
                                e.modulos[chave]
                                  ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-200"
                                  : "bg-gray-100 text-gray-400 line-through dark:bg-white/5 dark:text-gray-500"
                              }`}
                            >
                              {rotulo}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
